import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Mail, Loader2, CheckCircle2, Store as StoreIcon, Calendar as CalendarIcon, Clock, Zap } from "lucide-react";
import { adminApi, formatApiErrorDetail, mondayOf } from "@/lib/api";
import { fmtDate } from "@/components/status";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <span className="text-xs font-semibold uppercase tracking-wide text-[#28282E]/55">{label}</span>
      <div className={`mt-2 text-3xl font-bold ${tone || "text-[#28282E]"}`}>{value}</div>
    </div>
  );
}

function AutoReminderCard() {
  const [s, setS] = useState(null);
  const [running, setRunning] = useState(false);
  useEffect(() => { adminApi.getReminderSchedule().then(setS).catch(() => {}); }, []);
  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    try {
      const r = await adminApi.saveReminderSchedule({ enabled: next.enabled, hours_before_cutoff: next.hours_before_cutoff, include_customer_ids: next.include_customer_ids || null });
      setS((p) => ({ ...p, ...r.schedule }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const runNow = async () => {
    if (!window.confirm("Send cutoff reminders now to all stores currently missing an order for an upcoming window? This emails the ops inbox.")) return;
    setRunning(true);
    try {
      const r = await adminApi.runRemindersNow();
      toast.success(r.ran ? `${r.stores_notified} store(s) nudged (${r.emails_delivered} emailed)` : "Nothing to send right now");
      adminApi.getReminderSchedule().then(setS).catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setRunning(false); }
  };
  if (!s) return null;
  const last = s.last_run_summary || {};
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="auto-reminder-card">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[#28282E]"><Clock className="h-4 w-4 text-[#D46117]" /> Auto reminders</div>
      <label className="flex items-center justify-between rounded-xl bg-[#FBEEF4] px-3 py-2.5">
        <span className="text-sm font-semibold text-[#28282E]">Nudge missing stores before cutoff</span>
        <Switch checked={!!s.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="auto-reminder-enabled" />
      </label>
      <div className="mt-3 flex items-end gap-3">
        <div>
          <Label className="mb-1.5 block text-xs font-semibold text-[#28282E]/70">Hours before cutoff</Label>
          <input type="number" min={1} max={72} value={s.hours_before_cutoff}
            onChange={(e) => setS({ ...s, hours_before_cutoff: Number(e.target.value) })}
            onBlur={(e) => save({ hours_before_cutoff: Math.max(1, Math.min(72, Number(e.target.value) || 12)) })}
            data-testid="auto-reminder-hours" className="h-9 w-24 rounded-md border border-slate-200 px-3 text-sm" />
        </div>
        <Button onClick={runNow} disabled={running} data-testid="auto-reminder-run" variant="outline" className="rounded-full border-[#EBBAD1] text-[#28282E] hover:bg-[#FBEEF4]">
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />} Run now
        </Button>
      </div>
      {last.at ? <p className="mt-3 text-xs text-[#28282E]/70">Last run {fmtDate(last.at)} — {last.stores_notified || 0} store(s) nudged.</p>
        : <p className="mt-3 text-xs text-[#28282E]/55">Runs automatically every 30 min while enabled.</p>}
    </div>
  );
}

export default function MissingOrders() {
  const [week, setWeek] = useState(mondayOf());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);

  const load = () => {
    setLoading(true);
    adminApi.missingOrders(week)
      .then((d) => { setData(d); setSelected(new Set()); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
    adminApi.reminderLog(14).then(setLog).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [week]);

  const missing = data?.by_week?.missing_stores || [];
  const allSelected = missing.length > 0 && selected.size === missing.length;
  const toggle = (code) => setSelected((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(missing.map((m) => m.store_code)));

  const send = async () => {
    const codes = [...selected];
    if (!codes.length) return toast.error("Select at least one store to remind");
    setSending(true);
    try {
      const res = await adminApi.sendReminders({ store_codes: codes, week_commencing: week, message });
      toast.success(res.note ? `${res.delivered} sent · ${res.skipped} logged` : `${res.delivered} reminders sent`);
      // Optimistically show the just-sent reminders immediately
      const nowIso = new Date().toISOString();
      setLog((prev) => [
        ...codes.map((c) => ({ id: `${c}-${nowIso}`, store_code: c, sent_at: nowIso, delivered: res.delivered >= res.skipped })),
        ...prev,
      ]);
      setMessage("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="admin-missing-orders">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#28282E]">Missing Orders</h1>
          <p className="text-sm text-[#28282E]/70">Stores that haven't placed an order for the selected week. Send a one-click reminder.</p>
        </div>
        <div>
          <Label className="mb-1.5 block text-xs font-semibold text-[#28282E]/70">Week commencing</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="missing-week-input" className="w-44 justify-start font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" /> {fmtDate(week)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={new Date(`${week}T00:00:00`)} onSelect={(d) => d && setWeek(mondayOf(d))} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20 text-[#28282E]/55"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <Stat label="Expected" value={data?.by_week?.total_expected ?? 0} />
            <Stat label="Ordered" value={data?.by_week?.ordered ?? 0} tone="text-emerald-600" />
            <Stat label="Missing" value={data?.by_week?.missing ?? 0} tone="text-[#D46117]" />
          </div>

          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <div className="rounded-3xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
                <span className="text-sm font-bold text-[#28282E]">Stores yet to order ({missing.length})</span>
                {missing.length > 0 && (
                  <button onClick={toggleAll} data-testid="missing-select-all" className="text-xs font-semibold text-[#28282E] hover:underline">
                    {allSelected ? "Clear all" : "Select all"}
                  </button>
                )}
              </div>
              {missing.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-16 text-[#28282E]/55">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                  <p className="text-sm">Every active store has ordered this week. </p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {missing.map((s) => (
                    <li key={s.store_code} data-testid={`missing-row-${s.store_code}`} className="flex items-center gap-3 px-5 py-3">
                      <Checkbox checked={selected.has(s.store_code)} onCheckedChange={() => toggle(s.store_code)} data-testid={`missing-check-${s.store_code}`} />
                      <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#FBEEF4] text-[#28282E]"><StoreIcon className="h-4 w-4" /></span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold text-[#28282E]">{s.store_name}</div>
                        <div className="text-xs text-[#28282E]/70">{s.store_code}{s.location ? ` · ${s.location}` : ""}</div>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(s.delivery_days || []).map((d) => (
                          <span key={d} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-[#28282E]/70">{d}</span>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-4">
              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <div className="mb-2 flex items-center gap-2 text-sm font-bold text-[#28282E]"><Mail className="h-4 w-4 text-[#28282E]" /> Send reminder</div>
                <p className="mb-3 text-xs text-[#28282E]/70">{selected.size} store{selected.size === 1 ? "" : "s"} selected. Reminders go to the Hasfarm ops inbox.</p>
                <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Optional custom message (leave blank for the default reminder)…" data-testid="missing-message" />
                <Button onClick={send} disabled={sending || !selected.size} data-testid="missing-send-btn"
                  className="mt-3 w-full rounded-full bg-[#28282E] text-white hover:bg-[#1F1F24]">
                  {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Send reminders
                </Button>
              </div>

              <AutoReminderCard />

              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <div className="mb-3 text-sm font-bold text-[#28282E]">Recent reminders</div>
                {log.length === 0 ? (
                  <p className="text-xs text-[#28282E]/55">No reminders sent in the last 14 days.</p>
                ) : (
                  <ul className="space-y-2" data-testid="reminder-log">
                    {log.slice(0, 12).map((r) => (
                      <li key={r.id} className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-[#28282E]">{r.store_code}</span>
                        <span className="text-[#28282E]/55">{fmtDate(r.sent_at)}</span>
                        <span className={`rounded-full px-2 py-0.5 font-bold ${r.delivered ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{r.delivered ? "sent" : "logged"}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
